import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, FormControl, InputLabel, Select, MenuItem, Checkbox, Chip, Box } from '@mui/material';
import FilterAccordion from 'components/FilterAccordion';
import { SoldProductsFiltersState, FilterAction } from 'hooks/useSoldProductsFilters';
import { InAppSaleType, ReservationType, OrderStatus, orderStatus } from 'lib/models/order';
import useSections from 'hooks/useSections';
import useVendors from 'hooks/useVendors';

type Props = {
	filters: SoldProductsFiltersState;
	dispatch: React.Dispatch<FilterAction>;
};

const getInAppSaleType = (status: InAppSaleType): string => {
	switch (status) {
		case InAppSaleType.order:
			return 'Compra';
		case InAppSaleType.reservation:
			return 'Reserva';
		default:
			return status;
	}
};

const getReservationType = (status: ReservationType): string => {
	switch (status) {
		case ReservationType.parking:
			return 'Parking';
		case ReservationType.resort:
			return 'Resort';
		default:
			return status;
	}
};

const getStatusLabel = (status: string): string => {
	switch (status) {
		case OrderStatus.closed:
			return 'Entregado';
		case OrderStatus.active:
			return 'Pagado';
		case OrderStatus.cancelled:
			return 'Devuelto';
		default:
			return status;
	}
};

// Sentinel values for the isInAppSale select
const IS_IN_APP_SALE_ALL = 'all';
const IS_IN_APP_SALE_TRUE = 'true';
const IS_IN_APP_SALE_FALSE = 'false';

export default function OtherFilters({ filters, dispatch }: Props) {
	const { selectedStatuses, selectedVendors, selectedSections, isInAppSale, inAppSaleType, reservationType } = filters;
	const { t } = useTranslation();
	const { vendors } = useVendors();
	const { sections } = useSections();
	const handleClear = () => {
		dispatch({ type: 'CLEAR_OTHER_FILTERS' });
	};

	const isInAppSaleValue =
		isInAppSale === true ? IS_IN_APP_SALE_TRUE : isInAppSale === false ? IS_IN_APP_SALE_FALSE : IS_IN_APP_SALE_ALL;

	const handleIsInAppSaleChange = (value: string) => {
		if (value === IS_IN_APP_SALE_TRUE) {
			dispatch({ type: 'SET_IS_IN_APP_SALE', payload: true });
		} else if (value === IS_IN_APP_SALE_FALSE) {
			dispatch({ type: 'SET_IS_IN_APP_SALE', payload: false });
		} else {
			dispatch({ type: 'SET_IS_IN_APP_SALE', payload: null });
		}
	};

	return (
		<FilterAccordion title="Otros Filtros" onClear={handleClear} clearTooltip="Limpiar otros filtros">
			<Grid container spacing={2}>
				{/* Canal filter: All / App / Bar-Caja */}
				<Grid item xs={12} sm={isInAppSale === true ? (inAppSaleType === InAppSaleType.reservation ? 3 : 6) : 12}>
					<FormControl sx={{ width: '100%' }}>
						<InputLabel id="is-inapp-sale-filter-label">Canal</InputLabel>
						<Select
							labelId="is-inapp-sale-filter-label"
							id="is-inapp-sale-filter"
							value={isInAppSaleValue}
							onChange={(e) => handleIsInAppSaleChange(e.target.value)}
							label="Canal"
						>
							<MenuItem value={IS_IN_APP_SALE_ALL}>
								<em>Todos</em>
							</MenuItem>
							<MenuItem value={IS_IN_APP_SALE_TRUE}>App</MenuItem>
							<MenuItem value={IS_IN_APP_SALE_FALSE}>Bar / Caja</MenuItem>
						</Select>
					</FormControl>
				</Grid>

				{/* Tipo filter: only visible when Canal = App */}
				{isInAppSale === true && (
					<Grid item xs={12} sm={inAppSaleType === InAppSaleType.reservation ? 3 : 6}>
						<FormControl sx={{ width: '100%' }}>
							<InputLabel id="type-filter-label">Tipo</InputLabel>
							<Select
								labelId="type-filter-label"
								id="type-filter"
								value={inAppSaleType}
								onChange={(e) => dispatch({ type: 'SET_INAPP_SALE_TYPE', payload: e.target.value as InAppSaleType | '' })}
								label="Tipo"
							>
								<MenuItem value="">
									<em>Todos</em>
								</MenuItem>
								{[InAppSaleType.order, InAppSaleType.reservation].map((status) => (
									<MenuItem key={status} value={status}>
										{getInAppSaleType(status)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				)}

				{/* Tipo de reserva: only visible when Tipo = Reserva */}
				{isInAppSale === true && inAppSaleType === InAppSaleType.reservation && (
					<Grid item xs={12} sm={6}>
						<FormControl sx={{ width: '100%' }}>
							<InputLabel id="reservation-type-filter-label">Tipo de reserva</InputLabel>
							<Select
								labelId="reservation-type-filter-label"
								id="reservation-type-filter"
								value={reservationType}
								onChange={(e) =>
									dispatch({ type: 'SET_RESERVATION_TYPE', payload: e.target.value as ReservationType | '' })
								}
								label="Tipo de reserva"
							>
								<MenuItem value="">
									<em>Todos</em>
								</MenuItem>
								{[ReservationType.parking, ReservationType.resort].map((status) => (
									<MenuItem key={status} value={status}>
										{getReservationType(status)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				)}

				<Grid item xs={12} sm={4}>
					<FormControl sx={{ width: '100%' }}>
						<InputLabel id="status-filter-label">{t('common:status')}</InputLabel>
						<Select
							labelId="status-filter-label"
							id="status-filter"
							multiple
							value={selectedStatuses}
							onChange={(e) => dispatch({ type: 'SET_SELECTED_STATUSES', payload: e.target.value as OrderStatus[] })}
							label={t('common:status')}
							renderValue={(selected) => (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{selected.map((value) => {
										const label = getStatusLabel(value);
										return <Chip key={value} label={label} size="small" />;
									})}
								</Box>
							)}
						>
							{orderStatus.map((status) => {
								const label = getStatusLabel(status);
								return (
									<MenuItem key={status} value={status}>
										<Checkbox checked={selectedStatuses.indexOf(status) > -1} />
										{label}
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={4}>
					<FormControl sx={{ width: '100%' }}>
						<InputLabel id="vendor-filter-label">Usuario interno</InputLabel>
						<Select
							labelId="vendor-filter-label"
							id="vendor-filter"
							multiple
							value={selectedVendors}
							onChange={(e) => dispatch({ type: 'SET_SELECTED_VENDORS', payload: e.target.value as string[] })}
							label="Usuario interno"
							renderValue={(selected) => (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{selected.map((value) => (
										<Chip key={value} label={value} size="small" />
									))}
								</Box>
							)}
						>
							{vendors.map((vendor) => (
								<MenuItem key={vendor.username} value={vendor.username}>
									<Checkbox checked={selectedVendors.indexOf(vendor.username) > -1} />
									{vendor.username}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={4}>
					<FormControl sx={{ width: '100%' }}>
						<InputLabel id="section-filter-label">{t('common:typeoOfLocation')}</InputLabel>
						<Select
							labelId="section-filter-label"
							id="section-filter"
							multiple
							value={selectedSections}
							onChange={(e) => dispatch({ type: 'SET_SELECTED_SECTIONS', payload: e.target.value as string[] })}
							label={t('common:typeoOfLocation')}
							renderValue={(selected) => (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{selected.map((sectionId) => {
										const section = sections?.find((s: any) => s._id === sectionId);
										return <Chip key={sectionId} label={section?.title || sectionId} size="small" />;
									})}
								</Box>
							)}
						>
							{(sections || []).map((section: any) => (
								<MenuItem key={section._id} value={section._id}>
									<Checkbox checked={selectedSections.indexOf(section._id) > -1} />
									{section.title}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
		</FilterAccordion>
	);
}
